<script setup lang="ts">
import { computed, nextTick, onMounted, ref } from 'vue'
import { api, extractError } from '@/api/client'
import FaqSection from '@/components/support/FaqSection.vue'
import { useAutoRefresh } from '@/composables/useAutoRefresh'
import { useUnreadStore } from '@/stores/unread'
import { isTelegramWebApp } from '@/telegram/webapp'
import type { SupportMessage } from '@/types'
import { formatDateTime } from '@/utils/format'
import { linkifyText } from '@/utils/linkify'

const tgWebApp = isTelegramWebApp()

const messages = ref<SupportMessage[]>([])
const loading = ref(true)
const sending = ref(false)
const error = ref('')
const draft = ref('')
const files = ref<File[]>([])
const chatEl = ref<HTMLElement | null>(null)
const fileInput = ref<HTMLInputElement | null>(null)
const unreadStore = useUnreadStore()

async function load(markRead = true) {
  try {
    const { data } = await api.get<{
      success: boolean
      messages: SupportMessage[]
      unread_count?: number
    }>(`/api/user/messages?mark_read=${markRead ? '1' : '0'}`)
    if (data.success) {
      messages.value = data.messages || []
      unreadStore.setSupportUnread(data.unread_count ?? 0)
      await nextTick()
      scrollBottom()
    }
  } catch (e) {
    error.value = extractError(e, 'Не удалось загрузить сообщения')
  } finally {
    loading.value = false
  }
}

function scrollBottom() {
  if (chatEl.value) chatEl.value.scrollTop = chatEl.value.scrollHeight
}

function onFilesChange(e: Event) {
  const input = e.target as HTMLInputElement
  files.value = input.files ? [...input.files] : []
}

function isImageAttachment(fileType?: string): boolean {
  return !!fileType?.startsWith('image/')
}

async function send() {
  const text = draft.value.trim()
  if (!text && files.value.length === 0) return
  sending.value = true
  error.value = ''
  try {
    if (files.value.length > 0) {
      const fd = new FormData()
      if (text) fd.append('message', text)
      files.value.forEach((f, i) => fd.append(`attachment_${i}`, f, f.name))
      await api.post('/api/user/messages', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    } else {
      await api.post('/api/user/messages', { message: text })
    }
    draft.value = ''
    files.value = []
    if (fileInput.value) fileInput.value.value = ''
    await load(false)
  } catch (e) {
    error.value = extractError(e, 'Не удалось отправить')
  } finally {
    sending.value = false
  }
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault()
    void send()
  }
}

onMounted(() => void load())
useAutoRefresh(() => load(false), 15_000)

const chatBoxClass = computed(() =>
  tgWebApp
    ? 'support-chat-tg flex min-h-0 flex-1 flex-col items-stretch gap-3 overflow-y-auto overflow-x-hidden bg-black/[0.02] p-3'
    : 'flex max-h-[420px] min-h-[320px] flex-col items-stretch gap-3 overflow-y-auto overflow-x-hidden bg-black/[0.02] p-4',
)
</script>

<template>
  <div :class="tgWebApp ? 'support-page-tg flex min-h-0 flex-1 flex-col' : ''">
    <h1 class="page-title mb-4 shrink-0" :class="tgWebApp ? 'text-lg' : 'mb-6'">Поддержка</h1>

    <FaqSection v-if="!tgWebApp" />

    <div
      class="panel flex flex-col gap-0 overflow-hidden !p-0"
      :class="tgWebApp ? 'support-panel-tg min-h-0 flex-1' : 'gap-4'"
    >
      <div
        ref="chatEl"
        :class="chatBoxClass"
      >
        <div v-if="loading" class="text-sm text-muted">Загрузка…</div>
        <div v-else-if="messages.length === 0" class="text-sm text-muted">
          Напишите нам — ответим в этом чате.
        </div>
        <div
          v-for="msg in messages"
          :key="msg.id"
          class="min-w-0 max-w-[85%] rounded-lg px-3 py-2 text-sm"
          :class="msg.is_from_user
            ? 'self-end bg-primary text-white'
            : 'self-start border border-white-light bg-white text-black'"
        >
          <div
            v-if="msg.message"
            class="whitespace-pre-wrap break-words [overflow-wrap:anywhere]"
            v-html="linkifyText(msg.message)"
          />
          <div
            v-if="msg.attachments?.length"
            class="mt-2 flex flex-wrap gap-2"
          >
            <template v-for="att in msg.attachments" :key="att.id">
              <a
                v-if="isImageAttachment(att.file_type)"
                :href="att.download_url"
                target="_blank"
                rel="noopener"
                class="block"
              >
                <img
                  :src="att.download_url"
                  :alt="att.file_name"
                  class="max-h-48 max-w-full rounded border border-white/20 object-contain"
                />
              </a>
              <a
                v-else
                :href="att.download_url"
                target="_blank"
                rel="noopener"
                class="text-xs underline break-all"
                :class="msg.is_from_user ? 'text-white/90' : 'text-primary'"
              >
                {{ att.file_name }}
              </a>
            </template>
          </div>
          <div
            class="mt-1 flex items-center gap-1.5 text-[10px]"
            :class="msg.is_from_user ? 'justify-end' : ''"
          >
            <span :class="msg.is_from_user ? 'text-white/70' : 'text-muted'">
              {{ formatDateTime(msg.created_at) }}
            </span>
            <span
              v-if="msg.is_from_user"
              :title="msg.is_read_by_admin ? 'Прочитано' : 'Доставлено'"
              class="support-read-tick shrink-0"
              :class="msg.is_read_by_admin ? 'support-read-tick--read' : 'support-read-tick--sent'"
            >
              <svg
                v-if="msg.is_read_by_admin"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z" />
                <path d="M12.97 4.97a.75.75 0 0 1 1.07 1.05l-5.5 6.875a.75.75 0 0 1-1.08.02L7.324 10.384a.75.75 0 1 1 1.06-1.06l1.594 1.593 4.473-5.675z" />
              </svg>
              <svg
                v-else
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="14"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425z" />
              </svg>
            </span>
          </div>
        </div>
      </div>

      <div
        class="shrink-0 border-t border-white-light p-3"
        :class="tgWebApp ? 'support-composer-tg' : 'p-4'"
      >
        <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>
        <div v-if="files.length" class="mb-2 flex flex-wrap gap-2 text-xs text-muted">
          <span v-for="f in files" :key="f.name">{{ f.name }}</span>
        </div>
        <div :class="tgWebApp ? 'flex flex-col gap-2' : 'flex gap-2'">
          <textarea
            v-model="draft"
            :rows="tgWebApp ? 3 : 2"
            class="form-textarea w-full flex-1"
            :class="tgWebApp ? 'min-h-[72px]' : 'min-h-[60px]'"
            placeholder="Сообщение… (Enter — отправить, Shift+Enter — новая строка)"
            :disabled="sending"
            @keydown="onKeydown"
          />
          <div class="support-composer-actions">
            <button
              type="button"
              class="btn btn-secondary support-attach-btn"
              :class="tgWebApp ? '' : 'btn-sm shrink-0'"
              title="Прикрепить файл"
              @click="fileInput?.click()"
            >
              📎
            </button>
            <input
              ref="fileInput"
              type="file"
              multiple
              class="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt,.zip"
              @change="onFilesChange"
            />
            <button
              type="button"
              class="btn btn-primary support-send-btn"
              :class="tgWebApp ? '' : 'btn-sm shrink-0 self-end'"
              :disabled="sending || (!draft.trim() && !files.length)"
              @click="send"
            >
              {{ sending ? '…' : 'Отправить' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(a) {
  word-break: break-all;
}

.support-read-tick {
  display: inline-flex;
  align-items: center;
  color: #ffffff;
}

.support-read-tick--read {
  opacity: 1;
}

.support-read-tick--sent {
  opacity: 0.85;
}
</style>
